import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Trash2, Pencil, GripVertical } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { VideoDescriptionEditor } from './VideoDescriptionEditor';
import { PlaylistVideo } from '@/store/playlist/types';
import { cn } from '@/lib/utils';

interface PlaylistVideoAccordionProps {
  video: PlaylistVideo;
  videoData: {
    id: string;
    name: string;
    thumbnail: string;
    duration: number;
    views: number;
  };
  index: number;
  onRemove: () => void;
  onUpdateVideo: (updates: Partial<PlaylistVideo>) => void;
}

export const PlaylistVideoAccordion: React.FC<PlaylistVideoAccordionProps> = ({
  video,
  videoData,
  index,
  onRemove,
  onUpdateVideo,
}) => {
  useLanguage();
  const [isExpanded, setIsExpanded] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: video.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <TooltipProvider>
      <>
        <div 
          ref={setNodeRef}
          style={style}
          className="border border-[#E6ECF5] rounded-xl bg-white overflow-hidden"
        >
          {/* Header - sempre visível */}
          <div className="flex items-center gap-3 px-3 py-2.5">
          {/* Drag Handle */}
          <button
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground transition-colors touch-none flex-shrink-0"
          >
            <GripVertical className="h-5 w-5" />
          </button>

          {/* Ordem */}
          <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-[#2D6CF6] flex items-center justify-center">
            <span className="text-sm font-bold text-white">
              {index + 1}
            </span>
          </div>

          {/* Thumbnail */}
          <div className="relative w-28 h-16 flex-shrink-0 bg-gray-200 rounded-[8px] overflow-hidden">
            <img
              src={videoData.thumbnail}
              alt={videoData.name}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <h3 className="text-[15px] font-medium text-[#111] line-clamp-1">
              {videoData.name}
            </h3>
            <p className="text-sm text-[#6B7280] mt-0.5">
              {formatDuration(videoData.duration)} • {videoData.views.toLocaleString(getInterfaceLanguage())} {" "}{translateInterfaceText("visualizações")}</p>
          </div>

          {/* Controles */}
          <div className="flex items-center gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowDeleteDialog(true)}
                  className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{translateInterfaceText("Excluir vídeo da playlist")}</p>
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="h-8 w-8"
                >
                  <Pencil className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{translateInterfaceText("Editar informações do vídeo")}</p>
              </TooltipContent>
            </Tooltip>
          </div>
        </div>

        {/* Conteúdo expansível - editor */}
        {isExpanded && (
          <div className="border-t border-[#E6ECF5] p-4 bg-[#FAFBFC]">
            <VideoDescriptionEditor
              title={video.episodeTitle || ''}
              description={video.episodeDescription || ''}
              ctaButton={video.ctaButton}
              onTitleChange={(title) =>
                onUpdateVideo({ episodeTitle: title })
              }
              onDescriptionChange={(description) =>
                onUpdateVideo({ episodeDescription: description })
              }
              onCtaButtonChange={(cta) => onUpdateVideo({ ctaButton: cta })}
            />
          </div>
        )}
      </div>

        {/* Modal de confirmação de exclusão */}
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent className="w-auto max-w-[400px] mx-auto">
            <AlertDialogHeader>
              <AlertDialogTitle>{translateInterfaceText("Excluir vídeo?")}</AlertDialogTitle>
              <AlertDialogDescription>
                {translateInterfaceText("Você confirma que deseja excluir este vídeo da playlist?")}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{translateInterfaceText("Cancelar")}</AlertDialogCancel>
              <AlertDialogAction
                onClick={onRemove}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90 h-9 px-4"
              >
                {translateInterfaceText("Excluir")}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </>
    </TooltipProvider>
  );
};